import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { CartLine } from '../types/product';
interface CartContextValue {
  lines: CartLine[];
  isOpen: boolean;
  count: number;
  total: number;
  openCart: () => void;
  closeCart: () => void;
  addLine: (line: Omit<CartLine, 'id'>) => void;
  setQuantity: (id: string, quantity: number) => void;
  removeLine: (id: string) => void;
}
const CartContext = createContext<CartContextValue | null>(null);
export function CartProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [lines, setLines] = useState<CartLine[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const addLine = useCallback((line: Omit<CartLine, 'id'>) => {
    const id = [line.slug, line.optionLabel, line.colorwayName ?? ''].join('::');
    setLines(current => {
      const existing = current.find(l => l.id === id);
      if (existing) {
        return current.map(l => l.id === id ? {
          ...l,
          quantity: l.quantity + line.quantity
        } : l);
      }
      return [...current, {
        ...line,
        id
      }];
    });
    setIsOpen(true);
  }, []);
  const setQuantity = useCallback((id: string, quantity: number) => {
    setLines(current => quantity <= 0 ? current.filter(l => l.id !== id) : current.map(l => l.id === id ? {
      ...l,
      quantity
    } : l));
  }, []);
  const removeLine = useCallback((id: string) => {
    setLines(current => current.filter(l => l.id !== id));
  }, []);
  const value = useMemo<CartContextValue>(() => {
    const count = lines.reduce((sum, l) => sum + l.quantity, 0);
    const total = lines.reduce((sum, l) => sum + l.quantity * l.unitPrice, 0);
    return {
      lines,
      isOpen,
      count,
      total,
      openCart: () => setIsOpen(true),
      closeCart: () => setIsOpen(false),
      addLine,
      setQuantity,
      removeLine
    };
  }, [lines, isOpen, addLine, setQuantity, removeLine]);
  return <CartContext.Provider value={value} data-id="element-138" data-mp="6uq03t7jv3yq">{children}</CartContext.Provider>;
}
export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside CartProvider');
  return ctx;
}